import { cva } from "class-variance-authority";
import { TextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import aspectRatio from "components/utils/aspectRatio";
import { atom, useSetAtom } from "jotai";
import { CircleChevronLeftIcon, CircleChevronRightIcon } from "lucide-react";
import type { SVGProps } from "react";
import { useIntl } from "react-intl";

type PaginationParameters = {
  skipOffset: number;
  currentLimit: number;
};
type PaginationState = {
  loading?: boolean;
  totalRows: number;
  rowsInGrid: number;
  skipOffset: number;
  currentLimit: number;
  hasNextPage: boolean;
};
interface PaginationControlsProps {
  state: PaginationState;
  disabled?: boolean;
  onChange: (params: PaginationParameters) => void;
}

export type PaginationInputState = {
  limit: number;
  skipOffset: number;
};

export const paginationStateAtom = atom<PaginationState | null>(null);
export const paginationInputAtom = atom<PaginationInputState | null>({
  limit: 100,
  skipOffset: 0,
});

export function usePaginationInputControls() {
  const set = useSetAtom(paginationInputAtom);
  return {
    resetPagination: () => set((currentState) => ({ limit: currentState?.limit || 100, skipOffset: 0 })),
    skipOffset: (offset: number) => set((currentState) => ({ limit: currentState?.limit || 100, skipOffset: offset })),
  };
}

export default function PaginationControls({ state, disabled, onChange }: PaginationControlsProps) {
  const intl = useIntl();
  const loading = state?.loading || false;
  const currentPage = Math.floor(state.skipOffset / state.currentLimit) + 1;
  const areControlsDisabled = loading || state.totalRows === 0;
  const hasPreviousPage = state.skipOffset > 0;
  // const hasPagination = state.totalRows > state.currentLimit;
  const totalPages = Math.ceil(state.totalRows / state.currentLimit);
  // if (!hasPagination) {
  //   return null;
  // }
  return (
    <div className="flex items-center gap-x-2">
      <div
        className={classNames(
          "relative inline-flex h-10 items-center gap-x-2 rounded-xl border border-slate-200 bg-white px-2",
          "dark:border-slate-950 dark:bg-slate-700",
          {
            "animate-pulse": loading,
            "pointer-events-none opacity-50": disabled !== false,
          },
        )}
      >
        <button
          type="button"
          title={intl.formatMessage({ defaultMessage: "Página Anterior", id: "+zlRjZ" })}
          className="group rounded-xl disabled:pointer-events-none"
          disabled={!hasPreviousPage || areControlsDisabled}
          onClick={() => {
            onChange({ skipOffset: state.skipOffset - state.currentLimit, currentLimit: state.currentLimit });
          }}
        >
          <CircleChevronLeftIcon
            strokeWidth={1.65}
            absoluteStrokeWidth={true}
            className={classNames("size-6", skipOffsetButtonVariants())}
          />
        </button>
        <button
          type="button"
          title={intl.formatMessage({ defaultMessage: "Próxima Página", id: "N97TUd" })}
          className="group rounded-xl disabled:pointer-events-none"
          disabled={!state.hasNextPage || areControlsDisabled}
          onClick={() => {
            onChange({ skipOffset: state.skipOffset + state.currentLimit, currentLimit: state.currentLimit });
          }}
        >
          <CircleChevronRightIcon
            strokeWidth={1.65}
            absoluteStrokeWidth={true}
            className={classNames("size-6", skipOffsetButtonVariants())}
          />
        </button>
        {(currentPage || 0) >= 3 && (
          <button
            type="button"
            title={intl.formatMessage({ defaultMessage: "Primeira Página", id: "jlhzje" })}
            className="group rounded-xl disabled:pointer-events-none"
            disabled={!hasPreviousPage || areControlsDisabled}
            onClick={() => onChange({ skipOffset: 0, currentLimit: state.currentLimit })}
          >
            <FirstPageIcon
              strokeWidth={0.05}
              className={classNames(
                "fill-slate-100 text-slate-600 group-hover:fill-blue-100 group-hover:text-blue-600",
                "transition-transform duration-200 group-hover:scale-125",
                "group-disabled:text-slate-400 group-disabled:opacity-50",
                "dark:fill-slate-900 dark:text-slate-300 dark:group-disabled:opacity-20",
                "dark:group-hover:fill-orange-400/30 dark:group-hover:text-orange-200",
              )}
              {...aspectRatio({
                originalWidth: 29.639,
                originalHeight: 22.295,
                desiredHeight: 22,
              })}
            />
          </button>
        )}
        {totalPages > 0 && (
          <TextTooltip
            asChild={true}
            side="right"
            align="center"
            delayDuration={1500}
            contentClassName="font-bold"
            content={intl.formatMessage({
              defaultMessage: "Página atual",
              id: "SkryTh",
            })}
          >
            <span
              className={classNames(
                "flex min-h-6 select-none items-center justify-center rounded-md bg-sea-100 p-0.5 px-2 text-xs font-bold text-sea-600",
                "dark:bg-blue-400/20 dark:text-blue-100",
                "whitespace-nowrap",
              )}
            >
              {currentPage} / {totalPages}
            </span>
          </TextTooltip>
        )}
      </div>
    </div>
  );
}

const skipOffsetButtonVariants = cva(
  classNames(
    "fill-slate-100 stroke-slate-600 group-hover:fill-blue-100 group-hover:stroke-blue-600",
    "transition-transform duration-200 group-hover:scale-125",
    "group-disabled:stroke-slate-400 group-disabled:opacity-50",
    "dark:fill-slate-900 dark:stroke-slate-400 dark:group-disabled:opacity-20",
    "dark:group-hover:fill-transparent dark:group-hover:stroke-blue-300",
  ),
  {
    variants: {},
    defaultVariants: {},
  },
);

const FirstPageIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg width={29.639} height={22.295} xmlns="http://www.w3.org/2000/svg" {...props} viewBox="0 0 29.639 22.295">
    <path d="M9.6 22.275h10.078c7.138 0 9.6-2.666 9.6-9.238V9.238c0-6.572-2.462-9.238-9.6-9.238H9.6C2.46 0 0 2.666 0 9.238v3.8c0 6.571 2.46 9.237 9.6 9.237Z" />
    <g fillRule="nonzero" fill="currentColor">
      <path
        strokeWidth={0.5}
        stroke="currentColor"
        d="M9.57 16.484c-.537 0-.83-.322-.83-.859V6.777c0-.566.352-.879.82-.879h2.383c1.846 0 2.95 1.514 2.95 3.448 0 1.953-1.114 3.437-3.038 3.437H10.4v2.842c0 .527-.273.86-.83.86Zm.83-5.029h1.123c1.202 0 1.72-.898 1.72-2.11 0-1.26-.558-2.08-1.71-2.08H10.4v4.19Zm8.721 5.04a.846.846 0 0 1-.84-.84V7.528l-1.279 1.026a.552.552 0 0 1-.4.156c-.352 0-.596-.254-.596-.615 0-.293.146-.508.351-.664l1.504-1.202c.274-.205.664-.468 1.143-.468.576 0 .947.351.947.927v8.965c0 .469-.38.84-.83.84Z"
      />
    </g>
  </svg>
);
